import { useEffect, useId, useRef, useState } from 'react';
import { clearZapierLeadsContext } from '../zapier-leads-context.js';
import { api } from '../api.js';
import Beacon from './Beacon.jsx';
import Select from './Select.jsx';
import { companySubpage } from '../company-navigation.js';
import { requestNavigation } from '../shell/router.js';
import { ConfirmationDialog, useConfirmationDialog } from './ConfirmationDialog.jsx';
import './CompanySwitcher.css';

export function eligibleCompanyMemberships(memberships = []) {
  return memberships.filter((member) => member.companyId && member.company?.name
    && member.status === 'active' && member.company.status === 'active');
}

export default function CompanySwitcher({ session, navigate, route, mainRef, onSwitching }) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);
  const lock = useRef(false);
  const confirmation = useConfirmationDialog();
  const memberships = eligibleCompanyMemberships(session.user?.memberships);
  const activeId = session.user?.activeCompany?.id || '';
  useEffect(() => () => { requestId.current += 1; onSwitching(false); }, [onSwitching]);

  async function switchCompany(companyId, { confirmedLeave = false } = {}) {
    if (companyId === activeId || lock.current || !memberships.some((m) => m.companyId === companyId)) return;
    // A screen holding unsaved edits asks first (OGC-531); the switch
    // continues only if the person discards them.
    const destination = route.name === 'company' ? `company/${companySubpage(route)}` : 'home';
    if (!confirmedLeave && !requestNavigation(`#/${destination}`, () => switchCompany(companyId, { confirmedLeave: true }))) return;
    const main = mainRef.current;
    if (main?.querySelector('[aria-busy="true"], dialog[open]')) {
      setError('Finish the current operation before switching company.');
      return;
    }
    lock.current = true;
    const version = ++requestId.current;
    const name = memberships.find((m) => m.companyId === companyId)?.company.name;
    if (['curated-campaign', 'company', 'profile'].includes(route.name)) {
      const accepted = await confirmation.request({
        title: `Switch to ${name}?`,
        description: route.name === 'company' ? 'You will open this Company page in the selected company. Unsaved edits will be discarded.' : 'You will open the company’s Home page. Any unsaved edits on this page will be discarded.',
        confirmLabel: 'Switch company',
      });
      if (!accepted || version !== requestId.current) { lock.current = false; return; }
    }
    setBusy(true); setError(''); onSwitching(true);
    try {
      const context = await api.get('/api/saas/context', { headers: { 'x-company-id': companyId } });
      if (version !== requestId.current) return;
      if (context?.activeCompany?.id !== companyId
        || !(await session.setActiveTenantContext(context, { isCurrent: () => version === requestId.current }))) throw new Error('unverified_company');
      if (version !== requestId.current) return;
      clearZapierLeadsContext();
      const target = destination === 'company/members' && !['owner', 'admin'].includes(context.role) ? 'company/overview' : destination;
      navigate(target, { replace: true, force: confirmedLeave });
    } catch {
      if (version === requestId.current) setError('This company could not be opened. Your current company is unchanged. Try again.');
    } finally {
      lock.current = false;
      if (version === requestId.current) { setBusy(false); onSwitching(false); }
    }
  }

  if (!activeId || !memberships.length) return null;
  return <div className="bl-company-switcher" aria-busy={busy}>
    <label htmlFor={id}>Company</label>
    <Select id={id} aria-label="Switch company" value={activeId} disabled={busy || memberships.length < 2}
      onChange={(companyId) => switchCompany(companyId)}
      options={memberships.map((m) => ({ value: m.companyId, label: m.company.name }))} />
    <Beacon size="inline" active={busy} label="Opening company…" slowAfterMs={null} />
    {error && <p role="alert">{error}</p>}
    <ConfirmationDialog {...confirmation.dialogProps} />
  </div>;
}
